import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import type { SessionUser } from "../App";
import { ApiError, apiRequest, jsonBody, setCsrfToken } from "../api/client";

interface ProjectListItem {
  id: string;
  customer_id: string;
  customer_name: string;
  template_name: string;
  project_name: string;
  slug: string;
  public_url: string;
  status: string;
  revision: number;
  updated_at: string;
  published_at: string | null;
  can_publish?: number;
}

interface CustomerListItem {
  id: string;
  name: string;
  status: string;
  project_count: number;
  member_emails: string | null;
}

interface TemplateListItem { id: string; name: string; description: string; thumbnail: string; current_version: string }
interface PublishIssue { path: string; message: string; section: string }

interface DashboardData {
  summary: { customers?: number; projects?: number; drafts?: number; published?: number; storage_bytes?: number };
  recentEdits: Array<{ id: string; project_name: string; customer_name: string; updated_at: string }>;
  recentRsvp: Array<{ guest_name: string; attendance: string; project_name: string; created_at: string }>;
  recentWishes: Array<{ guest_name: string; message: string; project_name: string; created_at: string }>;
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function formatBytes(value = 0) {
  if (!value) return "0 MB";
  return `${(value / 1024 / 1024).toFixed(value > 1024 * 1024 * 100 ? 0 : 1)} MB`;
}

function makeSlug(value: string) {
  return value.normalize("NFKD").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/-{2,}/g, "-").slice(0, 100);
}

function splitPairName(value: string) {
  const parts = value.split(/\s+(?:&|and|dan)\s+/i).map((part) => part.trim()).filter(Boolean);
  return { groomName: parts[0] || "", brideName: parts[1] || "" };
}

function syncPairFields(form: HTMLFormElement | null) {
  if (!form) return;
  const groom = form.elements.namedItem("groomName") as HTMLInputElement | null;
  const bride = form.elements.namedItem("brideName") as HTMLInputElement | null;
  const projectName = form.elements.namedItem("projectName") as HTMLInputElement | null;
  const slug = form.elements.namedItem("slug") as HTMLInputElement | null;
  const pair = [groom?.value.trim(), bride?.value.trim()].filter(Boolean).join(" & ");
  if (projectName && !projectName.dataset.touched) projectName.value = pair;
  if (slug && !slug.dataset.touched) slug.value = makeSlug(pair);
}

export function Dashboard({ user }: { user: SessionUser }) {
  const isPlatform = user.platformRole === "owner" || user.platformRole === "admin";
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [projects, setProjects] = useState<ProjectListItem[]>([]);
  const [customers, setCustomers] = useState<CustomerListItem[]>([]);
  const [templates, setTemplates] = useState<TemplateListItem[]>([]);
  const [modal, setModal] = useState<"customer" | "project" | "prepare" | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [preparingProject, setPreparingProject] = useState<ProjectListItem | null>(null);
  const [pendingIssues, setPendingIssues] = useState<PublishIssue[]>([]);
  const [notice, setNotice] = useState("");
  const [noticeUrl, setNoticeUrl] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const requests: [Promise<{ ok: true; summary: DashboardData["summary"]; recentEdits: DashboardData["recentEdits"]; recentRsvp: DashboardData["recentRsvp"]; recentWishes: DashboardData["recentWishes"] }>, Promise<{ ok: true; projects: ProjectListItem[] }>, Promise<{ ok: true; templates: TemplateListItem[] }>] = [
        apiRequest("/api/dashboard"), apiRequest("/api/projects"), apiRequest("/api/templates")
      ];
      const [dashboardResponse, projectResponse, templateResponse] = await Promise.all(requests);
      setDashboard(dashboardResponse);
      setProjects(projectResponse.projects);
      setTemplates(templateResponse.templates);
      if (isPlatform) {
        const customerResponse = await apiRequest<{ ok: true; customers: CustomerListItem[] }>("/api/customers");
        setCustomers(customerResponse.customers);
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load the dashboard.");
    }
  }, [isPlatform]);

  useEffect(() => { void load(); }, [load]);

  const published = useMemo(() => projects.filter((project) => project.status === "published").length, [projects]);

  const openProjectModal = (customerId = customers[0]?.id || "") => {
    setSelectedCustomerId(customerId);
    setError("");
    setModal("project");
  };

  const createCustomer = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true); setError(""); setNoticeUrl("");
    const form = event.currentTarget;
    const values = Object.fromEntries([...new FormData(form)].filter(([, value]) => typeof value !== "string" || value.trim() !== ""));
    const customerAction = ((event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null)?.value || "continue";
    try {
      const response = await apiRequest<{ ok: true; customer: { id: string; name: string } }>("/api/customers", { method: "POST", body: jsonBody(values) });
      setSelectedCustomerId(response.customer.id);
      setNotice(customerAction === "continue" ? "Customer dibuat. Lengkapi undangan untuk membuka editor atau langsung publish." : "Customer workspace created.");
      await load();
      setModal(customerAction === "continue" ? "project" : null);
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Unable to create customer."); }
    finally { setBusy(false); }
  };

  const createProject = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true); setError(""); setNotice(""); setNoticeUrl("");
    const values = Object.fromEntries(new FormData(event.currentTarget));
    const action = ((event.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null)?.value || "edit";
    try {
      const response = await apiRequest<{ ok: true; project: { id: string; slug: string; status: string; publicUrl: string } }>("/api/projects", {
        method: "POST",
        body: jsonBody({ ...values, starter: "ready", publishNow: action === "publish", canPublish: false })
      });
      if (action === "edit") {
        window.location.assign(`/${response.project.slug}/admin`);
        return;
      }
      setModal(null);
      setNotice("Undangan berhasil dibuat dan sudah live.");
      setNoticeUrl(response.project.publicUrl);
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Unable to create project."); }
    finally { setBusy(false); }
  };

  const publishProject = async (project: ProjectListItem) => {
    setBusy(true); setError(""); setNotice(""); setNoticeUrl("");
    try {
      const response = await apiRequest<{ ok: true; publicUrl: string }>(`/api/projects/${project.id}/publish`, { method: "POST", body: "{}" });
      setNotice("Undangan berhasil dipublish.");
      setNoticeUrl(response.publicUrl);
      await load();
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === "PUBLISH_VALIDATION_FAILED") {
        const details = caught.details && typeof caught.details === "object" && "issues" in caught.details ? caught.details.issues : [];
        setPendingIssues(Array.isArray(details) ? details as PublishIssue[] : []);
        setPreparingProject(project);
        setModal("prepare");
        setError("Undangan lama ini belum memiliki data minimum. Lengkapi nama dan tanggal di bawah untuk langsung publish.");
      } else setError(caught instanceof Error ? caught.message : "Unable to publish.");
    } finally { setBusy(false); }
  };

  const prepareAndPublish = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!preparingProject) return;
    setBusy(true); setError(""); setNotice(""); setNoticeUrl("");
    try {
      const response = await apiRequest<{ ok: true; publicUrl: string }>(`/api/projects/${preparingProject.id}/prepare-and-publish`, {
        method: "POST",
        body: jsonBody(Object.fromEntries(new FormData(event.currentTarget)))
      });
      setModal(null); setPreparingProject(null); setPendingIssues([]);
      setNotice("Template siap-edit diterapkan dan undangan sekarang sudah live.");
      setNoticeUrl(response.publicUrl);
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Unable to prepare and publish this invitation."); }
    finally { setBusy(false); }
  };

  const pauseProject = async (project: ProjectListItem) => {
    setBusy(true); setError(""); setNotice(""); setNoticeUrl("");
    try {
      await apiRequest(`/api/projects/${project.id}/pause`, { method: "POST", body: "{}" });
      setNotice(`${project.project_name} is paused. Guests cannot open its public URL.`);
      await load();
    } catch (caught) { setError(caught instanceof Error ? caught.message : "Unable to pause the invitation."); }
    finally { setBusy(false); }
  };

  const logout = async () => {
    try { await apiRequest("/api/auth/logout", { method: "POST", body: "{}" }); } finally {
      setCsrfToken(null); window.location.assign("/login");
    }
  };

  const suggestedNames = preparingProject ? splitPairName(preparingProject.project_name) : { groomName: "", brideName: "" };

  return (
    <div className="platform-shell">
      <aside className="platform-sidebar">
        <a className="platform-brand" href="/dashboard"><span className="brand-mark">IS</span><span>invitedStudio</span></a>
        <nav aria-label="Platform navigation">
          <a className="active" href="/dashboard"><span>◫</span> Overview</a>
          <a href="#projects"><span>♡</span> Invitations</a>
          {isPlatform ? <a href="#customers"><span>♙</span> Customers</a> : null}
          <a href="/reference-preview" target="_blank"><span>◈</span> Template preview</a>
        </nav>
        <div className="sidebar-account">
          <span className="account-avatar">{user.displayName.slice(0, 2).toUpperCase()}</span>
          <div><strong>{user.displayName}</strong><small>{isPlatform ? "Platform admin" : "Customer workspace"}</small></div>
          <button type="button" onClick={logout} aria-label="Sign out">↗</button>
        </div>
      </aside>
      <main className="platform-main">
        <header className="dashboard-header">
          <div><p className="platform-kicker">{isPlatform ? "Platform overview" : "My wedding"}</p><h1>{isPlatform ? "Good to see you." : "Your invitation workspace"}</h1><p>{isPlatform ? "Create customers, edit their complete template, and publish every live invitation here." : "Edit, preview, and manage responses for your assigned wedding project."}</p></div>
          {isPlatform ? <div className="header-actions"><button className="platform-button secondary" onClick={() => setModal("customer")}>New customer</button><button className="platform-button primary" onClick={() => openProjectModal()} disabled={!customers.length}>New invitation</button></div> : null}
        </header>
        {error ? <p className="alert error" role="alert">{error}</p> : null}
        {notice ? <p className="alert success" role="status">{notice}{noticeUrl ? <> <a href={noticeUrl} target="_blank" rel="noreferrer">Open real website ↗</a></> : null}</p> : null}
        <section className="metric-grid" aria-label="Workspace summary">
          <article><span className="metric-icon sage">♡</span><p>Total invitations</p><strong>{dashboard?.summary.projects ?? projects.length}</strong><small>Across the workspace</small></article>
          <article><span className="metric-icon sand">◔</span><p>Draft invitations</p><strong>{dashboard?.summary.drafts ?? projects.filter((item) => item.status === "draft").length}</strong><small>Still being prepared</small></article>
          <article><span className="metric-icon green">✓</span><p>Published</p><strong>{dashboard?.summary.published ?? published}</strong><small>Available to guests</small></article>
          <article><span className="metric-icon rose">▱</span><p>{isPlatform ? "Media storage" : "Projects assigned"}</p><strong>{isPlatform ? formatBytes(dashboard?.summary.storage_bytes) : projects.length}</strong><small>{isPlatform ? "Stored securely in R2" : "Scoped to your account"}</small></article>
        </section>
        <section className="dashboard-grid">
          <div className="dashboard-card projects-card" id="projects">
            <div className="card-heading"><div><p className="platform-kicker">Invitation projects</p><h2>{isPlatform ? "All weddings" : "My wedding"}</h2></div><span>{projects.length} total</span></div>
            <div className="project-table-wrap">
              <table className="project-table">
                <thead><tr><th>Project</th>{isPlatform ? <th>Customer</th> : null}<th>Status</th><th>Last edited</th><th aria-label="Actions" /></tr></thead>
                <tbody>
                  {projects.map((project) => (
                    <tr key={project.id}>
                      <td><strong>{project.project_name}</strong><small>/{project.slug}/ · {project.template_name}</small></td>
                      {isPlatform ? <td>{project.customer_name}</td> : null}
                      <td><span className={`status-pill ${project.status}`}>{project.status === "unpublished" ? "paused" : project.status}</span></td>
                      <td>{formatDate(project.updated_at)}</td>
                      <td><div className="row-actions"><a href={`/${project.slug}/admin`}>Admin / Edit</a>{project.status === "published" ? <a href={project.public_url} target="_blank" rel="noreferrer">View live</a> : null}{(isPlatform || project.can_publish === 1) && project.status === "published" ? <button type="button" disabled={busy} onClick={() => pauseProject(project)}>Pause</button> : null}{(isPlatform || project.can_publish === 1) && project.status !== "published" ? <button type="button" disabled={busy} onClick={() => publishProject(project)}>{project.status === "unpublished" ? "Resume" : "Publish live"}</button> : null}</div></td>
                    </tr>
                  ))}
                  {!projects.length ? <tr><td colSpan={isPlatform ? 5 : 4}><div className="empty-dashboard"><span>♡</span><h3>No invitation projects yet</h3><p>{isPlatform ? "Create a customer, then use the ready-to-edit template or publish it directly." : "Ask the platform admin to assign your wedding project."}</p></div></td></tr> : null}
                </tbody>
              </table>
            </div>
          </div>
          <aside className="dashboard-card activity-card">
            <div className="card-heading"><div><p className="platform-kicker">Live activity</p><h2>Recent responses</h2></div></div>
            <div className="activity-list">
              {dashboard?.recentRsvp.slice(0, 3).map((item, index) => <article key={`${item.created_at}-${index}`}><span className="activity-dot green" /><div><strong>{item.guest_name} · {item.attendance}</strong><p>{item.project_name}</p><small>{formatDate(item.created_at)}</small></div></article>)}
              {dashboard?.recentWishes.slice(0, 3).map((item, index) => <article key={`${item.created_at}-${index}`}><span className="activity-dot rose" /><div><strong>{item.guest_name} left a wish</strong><p>{item.message}</p><small>{formatDate(item.created_at)}</small></div></article>)}
              {!dashboard?.recentRsvp.length && !dashboard?.recentWishes.length ? <p className="muted-copy">Guest responses will appear here after an invitation is published.</p> : null}
            </div>
          </aside>
        </section>
        {isPlatform ? <section className="dashboard-card customer-card" id="customers"><div className="card-heading"><div><p className="platform-kicker">Customer access</p><h2>Customer workspaces</h2></div><button className="text-button" type="button" onClick={() => setModal("customer")}>Add customer</button></div><div className="customer-chips">{customers.map((customer) => <article key={customer.id}><span>{customer.name.slice(0, 1).toUpperCase()}</span><div><strong>{customer.name}</strong><small>{customer.member_emails || "No login assigned"} · {customer.project_count} projects</small></div><button className="customer-create-invite" type="button" onClick={() => openProjectModal(customer.id)}>+ Invitation</button></article>)}</div></section> : null}
      </main>

      {modal ? <div className="platform-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setModal(null); }}><section className="platform-modal" role="dialog" aria-modal="true" aria-labelledby="modal-title"><button className="modal-close" type="button" onClick={() => setModal(null)} aria-label="Close">×</button>
        {modal === "customer" ? <form onSubmit={createCustomer}>
          <p className="platform-kicker">Customer workspace</p><h2 id="modal-title">Create customer</h2><p>Setelah customer dibuat, Anda dapat langsung membuat undangan siap-edit atau publish live.</p>
          <label><span>Customer name</span><input name="name" required minLength={2} /></label>
          <label><span>Owner email <small>optional</small></span><input name="ownerEmail" type="email" /></label>
          <label><span>Owner display name</span><input name="ownerName" /></label>
          <label><span>Temporary password</span><input name="temporaryPassword" type="password" minLength={12} /></label>
          <div className="modal-action-grid"><button className="platform-button secondary" name="customerAction" value="only" disabled={busy}>Customer only</button><button className="platform-button primary" name="customerAction" value="continue" disabled={busy}>{busy ? "Creating…" : "Continue to invitation →"}</button></div>
        </form> : null}

        {modal === "project" ? <form onSubmit={createProject}>
          <p className="platform-kicker">Ready-to-edit starter</p><h2 id="modal-title">Create wedding invitation</h2><p>Struktur editor lengkap seperti John & Melissa, memakai gambar template netral—tanpa menyalin data atau foto customer lain.</p>
          <label><span>Customer</span><select name="customerId" required value={selectedCustomerId} onChange={(event) => setSelectedCustomerId(event.target.value)}><option value="" disabled>Select a customer</option>{customers.map((customer) => <option value={customer.id} key={customer.id}>{customer.name}</option>)}</select></label>
          <label><span>Template</span><select name="templateId" required>{templates.map((template) => <option value={template.id} key={template.id}>{template.name} · v{template.current_version}</option>)}</select></label>
          <div className="field-grid"><label><span>Groom name</span><input name="groomName" required onChange={(event) => syncPairFields(event.currentTarget.form)} /></label><label><span>Bride name</span><input name="brideName" required onChange={(event) => syncPairFields(event.currentTarget.form)} /></label></div>
          <label><span>Wedding date</span><input name="eventDate" type="date" required /></label>
          <label><span>Project name</span><input name="projectName" required onChange={(event) => { event.currentTarget.dataset.touched = "true"; }} /></label>
          <label><span>Public URL</span><div className="slug-input-row"><span>invitedstudio.com/</span><input name="slug" required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" onChange={(event) => { event.currentTarget.dataset.touched = "true"; }} /></div></label>
          <div className="field-grid"><label><span>Language</span><select name="language" defaultValue="id"><option value="id">Bahasa Indonesia</option><option value="en">English</option><option value="zh">中文</option></select></label><label><span>Timezone</span><input name="timezone" defaultValue="Asia/Jakarta" required /></label></div>
          <div className="starter-summary"><span>✓</span><div><strong>Siap dibuka di editor dan responsive</strong><small>Cover, nama, tanggal, countdown, background template, SEO, desktop, tablet, dan phone sudah memiliki data awal.</small></div></div>
          <div className="modal-action-grid"><button className="platform-button secondary" name="projectAction" value="edit" disabled={busy}>Create & edit</button><button className="platform-button primary" name="projectAction" value="publish" disabled={busy}>{busy ? "Processing…" : "Create & publish live"}</button></div>
        </form> : null}

        {modal === "prepare" && preparingProject ? <form onSubmit={prepareAndPublish}>
          <p className="platform-kicker">Complete legacy draft</p><h2 id="modal-title">Prepare & publish {preparingProject.project_name}</h2><p>Proyek lama dibuat dari template kosong. Isi tiga data minimum ini; isi lain yang sudah Anda edit tetap dipertahankan.</p>
          {pendingIssues.length ? <div className="compact-issue-list">{pendingIssues.slice(0, 4).map((issue) => <span key={issue.path}>! {issue.message}</span>)}</div> : null}
          <div className="field-grid"><label><span>Groom name</span><input name="groomName" defaultValue={suggestedNames.groomName} required /></label><label><span>Bride name</span><input name="brideName" defaultValue={suggestedNames.brideName} required /></label></div>
          <label><span>Wedding date</span><input name="eventDate" type="date" required /></label>
          <div className="starter-summary"><span>✓</span><div><strong>Perubahan aman</strong><small>Hanya data minimum yang kosong dan placeholder yang dilengkapi. Draft lalu disimpan sebagai versi live pertama.</small></div></div>
          <div className="modal-action-grid"><a className="platform-button secondary" href={`/${preparingProject.slug}/admin`}>Open admin editor</a><button className="platform-button primary" disabled={busy}>{busy ? "Publishing…" : "Prepare & publish live"}</button></div>
        </form> : null}
      </section></div> : null}
    </div>
  );
}
